import React, { useState, useEffect } from 'react';
import { ArrowRight, Play, ChevronRight, Binary, Cpu, Activity, Gamepad2, Trophy, Music, Clock, Sparkles, TrendingUp, Eye, Smartphone, DollarSign, Video, Crown, Star, Layers, Plus, Globe, MessageCircle } from 'lucide-react';
import { motion, useScroll, useSpring, AnimatePresence, type Variants } from 'motion/react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { useAuth } from '@/context/auth-context';
import { apiFetch } from '@/lib/api';
import { HeroSection } from '@/components/landing/HeroSection';
import { PricingSection } from '@/components/landing/PricingSection';
import WishlistSection from '@/components/landing/WishlistSection';
import { BlogSection } from '@/components/blog/BlogSection';
import { HomeFAQSection } from '@/components/landing/HomeFAQSection';
import { DiscordCTA } from '@/components/DiscordCTA';
import InternalLinksSection from '@/components/InternalLinksSection';
import { RelatedToolsSection } from '@/components/RelatedToolsSection';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { cn, getActiveRecipe } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import SpawnerPreview from '@/components/spawner-preview';
import EscapePreview from '@/components/escape-preview';
import TrailPreview from '@/components/trail-preview';
import DrawerPreview from '@/components/drawer-preview';
import RhythmicPreview from '@/components/rhythmic-preview';
import { BackgroundAnimation } from '@/components/background-animation';

const Home: React.FC = () => {
  const { user, loading } = useAuth();
  const [wishlistedPlans, setWishlistedPlans] = useState<Set<string>>(new Set());
  const [selectedWishlistPlan, setSelectedWishlistPlan] = useState<string>('general');

  useEffect(() => {
    const saved = localStorage.getItem('fmg_wishlist');
    if (saved) setWishlistedPlans(new Set(JSON.parse(saved)));
  }, []);

  const addToWishlist = async (plan: string) => {
    if (wishlistedPlans.has(plan)) return;
    const newWishlist = new Set(wishlistedPlans);
    newWishlist.add(plan);
    setWishlistedPlans(newWishlist);
    localStorage.setItem('fmg_wishlist', JSON.stringify(Array.from(newWishlist)));
    if (user) {
      try {
        await apiFetch('/api/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: user.email, name: user.username, plan: plan })
        });
      } catch (err) { console.error('Failed to sync wishlist:', err); }
    }
  };

  const handleSelectPlan = (planId: string) => {
    setSelectedWishlistPlan(planId);
  };

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  const sectionVariants: Variants = {
    hidden: { opacity: 0, y: 40 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const cardVariants: Variants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const modes = [
    {
      id: "spawner",
      title: "Spawner Mode",
      shortDesc: "Classic physics-driven experience",
      fullDesc: "The classic, physics-driven experience. Balls bounce around a container, increasing speed and potentially spawning new balls upon collision, creating a chaotic and mesmerizing \"fountain\" effect.",
      preview: <SpawnerPreview />,
      glass: "glass-amber",
      hoverGlow: "hover:border-black"
    },
    {
      id: "escape",
      title: "Escape Mode",
      shortDesc: "Breakout-style timing challenge",
      fullDesc: "A challenging \"breakout\" style mode. You must break through multiple concentric rotating rings, each with a gap, to escape the container. It features time challenges and speed multipliers.",
      preview: <EscapePreview />,
      glass: "glass-cyan",
      hoverGlow: "hover:border-black"
    },
    {
      id: "rhythmic",
      title: "Rhythmic Mode",
      shortDesc: "Music-synced visual instrument",
      fullDesc: "A music-synced experience where every bounce triggers a note from a selected melody. The container often pulses or scales based on the audio rhythm, turning the simulation into a visual instrument.",
      preview: <RhythmicPreview />,
      glass: "glass-amber",
      hoverGlow: "hover:border-black"
    },
    {
      id: "hybrid",
      title: "Rhythmic Escape (Hybrid)",
      shortDesc: "Audio-reactive ring-breaking",
      fullDesc: "This mode combines the audio-reactive nature of Rhythmic mode with the ring-breaking mechanics of Escape mode. It also includes a \"Shield\" mechanic to protect the center until certain conditions are met.",
      preview: <EscapePreview />, // Reusing for visual consistency
      glass: "glass-pink",
      hoverGlow: "hover:border-black"
    },
    {
      id: "drawer",
      title: "Drawer Mode",
      shortDesc: "Create complex geometric trails",
      fullDesc: "A creative mode where balls leave persistent trails on the screen, \"drawing\" complex geometric patterns as they follow physics-based curvatures and bounce off boundaries.",
      preview: <DrawerPreview />,
      glass: "glass-purple",
      hoverGlow: "hover:border-black"
    },
    {
      id: "trail",
      title: "Trail Mode",
      shortDesc: "Aesthetic motion ribbons",
      fullDesc: "Focuses on the visual aesthetic of motion. It maximizes the trailing effects and color transitions of the balls, creating smooth, flowing ribbons of light that occupy the Entire screen.",
      preview: <TrailPreview />,
      glass: "glass-pink",
      hoverGlow: "hover:border-black"
    }
  ];

  const [expandedMode, setExpandedMode] = useState<string | null>(null);

  const recipe = getActiveRecipe();

  if (loading) return <div className={cn("min-h-screen flex items-center justify-center font-sans text-xs uppercase tracking-[0.3em] animate-pulse text-muted-foreground", recipe.bg, recipe.text)}>Initializing Systems...</div>;

  const scrollToWishlist = () => {
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToFinalWishlist = () => {
    document.getElementById('wishlist-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className={cn("flex flex-col overflow-hidden selection:bg-primary selection:text-primary-foreground", recipe.bg, recipe.text)}>
      <SEO 
        title="Home"
        description="Create viral satisfying ball simulations, ASMR physics videos, and faceless shorts with our online physics video generator. Perfect for TikTok, Reels, and YouTube Shorts."
        keywords="ball simulation, ball simulator, satisfying physics simulation, ASMR video generator, faceless video generator, viral shorts creator, physics animation maker, YouTube shorts generator, TikTok physics videos"
        canonical="/"
      />
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-accent z-[100] origin-left"
        style={{ scaleX }}
      />
      <BackgroundAnimation />
      <HeroSection user={user} />

      {/* Creator Studio Section - Right After Hero */}
      <motion.section 
        id="creator-studio-section"
        aria-label="Hypnotic Ball Creator Studio Features"
        className="py-24 relative overflow-hidden"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={sectionVariants}
      >
        <div className="absolute inset-0 z-10 pointer-events-none [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)]">
           <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-[conic-gradient(from_0deg,#00e5ff_0%,transparent_10%,transparent_90%,#d500f9_100%)] opacity-[0.03] animate-[spin_20s_linear_infinite]" />
        </div>
        <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-r from-[#00e5ff]/5 via-transparent to-[#ff0080]/5 -z-10" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#ffea00]/5 blur-[200px] -z-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 md:mb-20 text-center relative z-10">
           <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-50 border border-cyan-100 mb-6 font-sans text-[10px] uppercase tracking-[0.3em] text-cyan-600">
             <Activity className="w-3.5 h-3.5" />
             <span>Live Now</span>
           </div>
           <h2 className="text-4xl sm:text-6xl md:text-8xl font-bold font-display text-zinc-950 mb-6 md:mb-8 uppercase tracking-tighter leading-[0.9] md:leading-[0.8]"><span className="text-cyan-600">Creator</span> <span className="text-zinc-400">Studio</span></h2>
           <p className="text-zinc-500 text-xs sm:text-sm max-w-2xl mx-auto font-medium leading-relaxed font-sans uppercase tracking-widest mb-10 md:mb-12 px-4">
             Build high-retention physics simulations. Customize every force, color, and sound to create viral content for social platforms.
           </p>
           <Link to="/dashboard" className={cn(buttonVariants({ size: "lg" }), "rounded-full px-12 h-16 bg-cyan-600 text-white hover:bg-cyan-700 transition-all shadow-2xl shadow-cyan-600/20 font-bold uppercase tracking-[0.3em] text-xs font-sans border border-cyan-500/50")}>
             Enter Studio <ArrowRight className="ml-3 w-5 h-5" />
           </Link>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            <AnimatePresence mode="popLayout">
              {modes.map((mode) => (
                <motion.div
                  key={mode.id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  onClick={() => setExpandedMode(expandedMode === mode.id ? null : mode.id)}
                  className="group relative rounded-[2rem] overflow-hidden transition-all duration-500 cursor-pointer border border-zinc-950/10 hover:border-cyan-500 shadow-2xl shadow-cyan-500/5 bg-white aspect-square"
                >
                  <div className="absolute inset-0 z-0 scale-[1.02] group-hover:scale-105 transition-transform duration-700 opacity-60 group-hover:opacity-100">
                    {mode.preview}
                  </div>
                  
                  <div className={cn(
                    "absolute inset-0 z-10 transition-all duration-500 p-6 md:p-8 flex flex-col justify-end",
                    expandedMode === mode.id ? "bg-white/90 backdrop-blur-md" : "bg-gradient-to-t from-white via-white/60 to-transparent"
                  )}>
                    <motion.div layout className="flex justify-between items-end">
                      <div className="pr-4">
                        <h3 className="text-zinc-950 font-bold text-xl md:text-2xl uppercase tracking-tighter leading-none">{mode.title}</h3>
                        <p className={cn("text-zinc-600 text-[9px] md:text-[10px] font-black uppercase tracking-[0.2em] mt-2 transition-opacity", expandedMode === mode.id ? "opacity-0 h-0" : "opacity-100")}>{mode.shortDesc}</p>
                      </div>
                      <div className="mb-0 md:mb-1">
                        <motion.div 
                          animate={{ rotate: expandedMode === mode.id ? 135 : 0 }}
                          className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-cyan-600 flex items-center justify-center text-white shadow-xl shadow-cyan-600/20"
                        >
                           <Plus className="w-4 h-4 md:w-5 md:h-5" />
                        </motion.div>
                      </div>
                    </motion.div>
                    
                    <AnimatePresence>
                      {expandedMode === mode.id && (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 20 }}
                          className="mt-6"
                        >
                          <div className="pt-6 border-t border-cyan-100">
                            <p className="text-zinc-700 text-xs md:text-sm leading-relaxed font-sans line-clamp-4">{mode.fullDesc}</p>
                            <Link 
                              to="/studio"
                              className="mt-6 inline-flex items-center gap-2 text-cyan-600 font-bold text-[10px] md:text-xs uppercase tracking-widest hover:gap-4 transition-all"
                            >
                              Launch Studio <ArrowRight className="w-4 h-4" />
                            </Link>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              ))}

              {!expandedMode && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="group relative rounded-[2rem] overflow-hidden aspect-square bg-white border border-zinc-950/10 hover:border-cyan-500 transition-all duration-500 shadow-2xl shadow-cyan-500/5 flex flex-col items-center justify-center text-center p-10"
                >
                  <div className="w-16 h-16 rounded-3xl bg-cyan-50 border border-cyan-100 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-90 group-hover:bg-cyan-600 transition-all duration-500">
                    <Plus className="w-8 h-8 text-cyan-300 group-hover:text-white transition-all" />
                  </div>
                  <h3 className="text-zinc-950 font-bold text-xl uppercase tracking-tight">More Modes Soon</h3>
                  <p className="text-zinc-500 text-xs mt-2 max-w-[200px]">New physics experiences in development for creators.</p>
                  
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.section>

      {/* Creator Economy / Revenue Section */}
      <section 
        id="creator-economy-section"
        aria-label="Creator Economy and Revenue Opportunities"
        className="py-24 bg-transparent relative overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-50 border border-cyan-100 mb-6 font-sans text-[10px] uppercase tracking-widest text-cyan-600">
                  <TrendingUp className="w-3.5 h-3.5" />
                  <span>Creator Economy</span>
                </div>
                <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold text-zinc-950 mb-8 uppercase tracking-tighter leading-[0.9] md:leading-[0.8]"><span className="text-cyan-600">Build a 6-Figure Empire</span> Without Showing Your Face.</h2>
                <p className="text-zinc-500 text-base sm:text-lg mb-12 leading-relaxed max-w-xl">
                  "Oddly Satisfying" is one of the most profitable niches on the internet. Our Creator Studio plan gives you the tools to dominate Shorts, TikTok, and Reels.
                </p>

              <div className="space-y-10">
                <div className="flex gap-6 group">
                  <div className="flex-shrink-0 w-14 h-14 rounded-3xl bg-cyan-50 border border-cyan-100 flex items-center justify-center group-hover:bg-cyan-600 group-hover:text-white transition-all duration-500 text-cyan-600">
                    <Eye className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-zinc-950 font-bold font-display text-xl mb-1 uppercase tracking-tight">Unmatched Retention</h3>
                    <p className="text-zinc-500 text-sm max-w-md font-sans">Physics simulations hold attention spans longer than almost any other content type.</p>
                  </div>
                </div>
                <div className="flex gap-6 group">
                   <div className="flex-shrink-0 w-14 h-14 rounded-3xl bg-cyan-50 border border-cyan-100 flex items-center justify-center group-hover:bg-cyan-600 group-hover:text-white transition-all duration-500 text-cyan-600">
                    <Smartphone className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-zinc-950 font-bold font-display text-xl mb-1 uppercase tracking-tight">Short-Form Dominance</h3>
                    <p className="text-zinc-500 text-sm max-w-md font-sans">Perfect for TikTok and Reels. One 4K render, three massive revenue streams.</p>
                  </div>
                </div>
                <div className="flex gap-6 group">
                   <div className="flex-shrink-0 w-14 h-14 rounded-3xl bg-cyan-50 border border-cyan-100 flex items-center justify-center group-hover:bg-cyan-600 group-hover:text-white transition-all duration-500 text-cyan-600">
                    <DollarSign className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-zinc-950 font-bold font-display text-xl mb-1 uppercase tracking-tight">High RPM Niche</h3>
                    <p className="text-zinc-500 text-sm max-w-md font-sans">Clean, brand-safe content attracts premium advertisers and higher payouts per view.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-10 bg-cyan-400/10 rounded-[4rem] blur-[120px]" />
              <div className="bg-white border border-zinc-950/10 rounded-[2rem] p-10 relative shadow-2xl shadow-black/5 backdrop-blur-xl">
                 <div className="flex justify-between items-start mb-12 flex-wrap gap-4">
                   <div>
                     <div className="flex items-center gap-3 mb-3">
                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                        <p className="text-[10px] text-zinc-400 uppercase font-bold font-sans tracking-widest">Global Payout Mode</p>
                     </div>
                     <p className="text-6xl font-bold font-display text-black tracking-tighter leading-none">$12,450.00</p>
                     <p className="text-zinc-400 text-[10px] font-bold font-sans uppercase tracking-[0.2em] mt-3">Monthly Revenue Est.</p>
                   </div>
                   <div className="flex flex-col items-end gap-2 md:gap-3">
                      <div className="px-4 py-2 bg-emerald-500 text-white text-[10px] font-bold font-sans rounded-full uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-emerald-500/20">
                        <TrendingUp className="w-3 h-3" />
                        +124% Growth
                      </div>
                      <div className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-full shadow-lg shadow-red-500/20">
                         <Video className="w-3.5 h-3.5 fill-white" />
                         <span className="text-[10px] font-bold font-sans uppercase tracking-widest leading-none">Fund Verified</span>
                      </div>
                   </div>
                 </div>

                 <div className="h-48 flex items-end justify-between gap-2.5 mb-12 px-2 relative z-0">
                    {[35, 55, 45, 70, 60, 85, 100, 80, 95, 75, 90, 85].map((h, i) => (
                      <div key={i} className="flex-1 bg-neutral-100 rounded-lg relative group overflow-hidden" style={{height: `${h}%`}}>
                         <div 
                           className="absolute inset-0 bg-gradient-to-t from-black via-zinc-800 to-zinc-700 opacity-20 group-hover:opacity-40 transition-all duration-300 pointer-events-none"
                         />
                         <motion.div 
                            initial={{ height: 0 }}
                            whileInView={{ height: '100%' }}
                            transition={{ duration: 1, delay: i * 0.05 }}
                            className="absolute inset-0 bg-gradient-to-t from-cyan-400 to-purple-500 opacity-60 mix-blend-overlay group-hover:opacity-100 transition-opacity"
                         />
                      </div>
                    ))}
                 </div>

                 <div className="grid grid-cols-3 gap-4 md:gap-8 border-t border-black/5 pt-8 md:pt-10">
                    <div className="text-center group">
                      <p className="text-zinc-400 text-[8px] md:text-[9px] mb-2 uppercase tracking-widest font-bold font-sans group-hover:text-black transition-colors">Views</p>
                      <p className="text-black font-bold font-display text-xl md:text-2xl tracking-tighter">2.4M</p>
                    </div>
                    <div className="text-center border-x border-black/5 group">
                      <p className="text-zinc-400 text-[8px] md:text-[9px] mb-2 uppercase tracking-widest font-bold font-sans group-hover:text-black transition-colors">Watch Time</p>
                      <p className="text-black font-bold font-display text-xl md:text-2xl tracking-tighter">45k<span className="text-[10px] font-bold opacity-30 px-1">HRS</span></p>
                    </div>
                    <div className="text-center group">
                      <p className="text-zinc-400 text-[8px] md:text-[9px] mb-2 uppercase tracking-widest font-bold font-sans group-hover:text-black transition-colors">Subs</p>
                      <p className="text-black font-bold font-display text-xl md:text-2xl tracking-tighter">+8.2k</p>
                    </div>
                 </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Global Creator Community Section */}
      <section 
        id="global-community-section"
        aria-label="Global Creator Community"
        className="py-32 relative overflow-hidden bg-white"
      >
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-cyan-400/5 blur-[120px] rounded-full -z-10" />
        
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-50 border border-cyan-100 mb-6 font-sans text-[10px] uppercase tracking-[0.3em] text-cyan-600 mx-auto">
              <Globe className="w-3.5 h-3.5" />
              <span>Global Impact</span>
            </div>
            <h2 className="text-4xl md:text-7xl font-bold font-display text-zinc-950 mb-6 uppercase tracking-tighter leading-none">
              10K+ Community <br/>
              <span className="text-cyan-600">Across 10M+ Reach</span>
            </h2>
            <p className="text-zinc-500 text-sm max-w-xl mx-auto font-sans tracking-tight leading-relaxed">
              Join thousands of creators using Creator Studio to fuel their faceless channels, automated brands, and oddly satisfying content empires.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            {/* YouTube Card */}
            <motion.a 
              href="https://youtube.com/@funcirclegames" 
              target="_blank" 
              rel="noreferrer"
              whileHover={{ y: -5 }}
              className="group relative bg-white rounded-[2rem] p-10 border border-zinc-950/10 overflow-hidden flex flex-col items-center text-center transition-all hover:border-black shadow-2xl shadow-black/5 backdrop-blur-xl"
            >
              <div className="absolute top-0 right-0 p-8 opacity-[0.03] group-hover:opacity-[0.07] transition-opacity">
                <Video className="w-48 h-48 -rotate-12" />
              </div>
              
              <div className="w-16 h-16 bg-neutral-100 rounded-3xl flex items-center justify-center p-4 mb-8 border border-black/5 group-hover:bg-red-600 transition-all duration-500">
                <Video className="w-full h-full text-black group-hover:text-white" />
              </div>
              
              <div className="space-y-4">
                <h3 className="text-3xl font-bold font-display tracking-tight">YouTube Creators</h3>
                <p className="text-zinc-400 text-xs font-bold tracking-widest uppercase">@funcirclegames</p>
                
                <div className="flex gap-4 pt-4">
                  <div className="px-4 py-2 bg-white rounded-2xl border border-zinc-100 shadow-sm">
                    <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-widest mb-1">Subscribers</p>
                    <p className="text-xl font-bold font-display tracking-tighter">4k+</p>
                  </div>
                  <div className="px-4 py-2 bg-white rounded-2xl border border-zinc-100 shadow-sm">
                    <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-widest mb-1">Monthly Views</p>
                    <p className="text-xl font-bold font-display tracking-tighter">1.2M+</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-10 flex items-center gap-3 text-red-600 font-bold text-xs uppercase tracking-widest">
                Explore Content <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
              </div>
            </motion.a>

            {/* Instagram Card */}
            <motion.a 
              href="https://instagram.com/funcirclegames" 
              target="_blank" 
              rel="noreferrer"
              whileHover={{ y: -5 }}
              className="group relative bg-white rounded-[2rem] p-10 border border-zinc-950/10 overflow-hidden flex flex-col items-center text-center transition-all hover:border-black shadow-2xl shadow-black/5 backdrop-blur-xl"
            >
              <div className="absolute top-0 right-0 p-8 opacity-[0.03] group-hover:opacity-[0.07] transition-opacity">
                <Smartphone className="w-48 h-48 -rotate-12" />
              </div>

              <div className="w-16 h-16 bg-neutral-100 rounded-3xl flex items-center justify-center p-4 mb-8 border border-black/5 group-hover:bg-gradient-to-br group-hover:from-purple-600 group-hover:via-pink-600 group-hover:to-amber-500 transition-all duration-500">
                <Smartphone className="w-full h-full text-black group-hover:text-white" />
              </div>
              
              <div className="space-y-4">
                <h3 className="text-3xl font-bold font-display tracking-tight">Short-Form Elite</h3>
                <p className="text-zinc-400 text-xs font-bold tracking-widest uppercase">@funcirclegames</p>
                
                <div className="flex gap-4 pt-4">
                  <div className="px-4 py-2 bg-white rounded-2xl border border-zinc-100 shadow-sm">
                    <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-widest mb-1">Followers</p>
                    <p className="text-xl font-bold font-display tracking-tighter">5k+</p>
                  </div>
                  <div className="px-4 py-2 bg-white rounded-2xl border border-zinc-100 shadow-sm">
                    <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-widest mb-1">Avg Engagement</p>
                    <p className="text-xl font-bold font-display tracking-tighter">8.5%</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-10 flex items-center gap-3 text-pink-600 font-bold text-xs uppercase tracking-widest">
                Explore Content <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
              </div>
            </motion.a>

            {/* Discord Card */}
            <motion.a 
              href="https://discord.gg/cdwNFdbx" 
              target="_blank" 
              rel="noopener noreferrer"
              whileHover={{ y: -5 }}
              className="group relative bg-white rounded-[2rem] p-10 border border-zinc-950/10 overflow-hidden flex flex-col items-center text-center transition-all hover:border-black shadow-2xl shadow-black/5 backdrop-blur-xl md:col-span-2"
            >
              <div className="absolute top-0 right-0 p-8 opacity-[0.03] group-hover:opacity-[0.07] transition-opacity">
                <MessageCircle className="w-48 h-48 -rotate-12" />
              </div>

              <div className="w-16 h-16 bg-neutral-100 rounded-3xl flex items-center justify-center p-4 mb-8 border border-black/5 group-hover:bg-[#5865F2] transition-all duration-500">
                <MessageCircle className="w-full h-full text-black group-hover:text-white" />
              </div>
              
              <div className="space-y-4">
                <h3 className="text-3xl font-bold font-display tracking-tight">Discord Community</h3>
                <p className="text-zinc-400 text-xs font-bold tracking-widest uppercase">Beta Testing · Bug Reports · Feature Requests</p>
                
                <div className="flex gap-4 pt-4 justify-center">
                  <div className="px-4 py-2 bg-white rounded-2xl border border-zinc-100 shadow-sm">
                    <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-widest mb-1">Members</p>
                    <p className="text-xl font-bold font-display tracking-tighter">Growing</p>
                  </div>
                  <div className="px-4 py-2 bg-white rounded-2xl border border-zinc-100 shadow-sm">
                    <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-widest mb-1">Channels</p>
                    <p className="text-xl font-bold font-display tracking-tighter">Active</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-10 flex items-center gap-3 text-[#5865F2] font-bold text-xs uppercase tracking-widest">
                Join Community <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
              </div>
            </motion.a>
          </div>

          <div className="mt-20 pt-20 border-t border-zinc-100 flex flex-wrap justify-center gap-12 sm:gap-24 opacity-40 grayscale hover:grayscale-0 transition-all duration-700">
             <div className="flex items-center gap-3">
               <Video className="w-6 h-6" />
               <span className="font-display font-bold text-2xl tracking-tighter">TIKTOK</span>
             </div>
             <div className="flex items-center gap-3">
               <Crown className="w-6 h-6" />
               <span className="font-display font-bold text-2xl tracking-tighter">REELS</span>
             </div>
             <div className="flex items-center gap-3">
               <Activity className="w-6 h-6" />
               <span className="font-display font-bold text-2xl tracking-tighter">TRENDS</span>
             </div>
          </div>
        </div>
      </section>

      <PricingSection user={user} onSelectPlan={handleSelectPlan} />
      
      <WishlistSection selectedPlan={selectedWishlistPlan} />

      <HomeFAQSection />
      
      <BlogSection />

      {/* Creator Tools Navigation */}
      <RelatedToolsSection />

      {/* Discord Community */}
      <DiscordCTA />

      {/* Sitewide Internal Links */}
      <InternalLinksSection 
        show={['pricing', 'tutorials', 'help', 'blog', 'showcase', 'updates', 'faq', 'discord', 'case-studies']}
        title="Explore Everything"
        subtitle="Your Complete Resource Hub"
      />
    </div>
  );
};

export default Home;
